import {EventsListContext} from 'modules/events/context';
import {getEvents, useGetEventsFilterQuery} from 'modules/events/services';
import {FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {getEventsApiQuery, getEventsGroupedByDay} from 'modules/events/helpers';
import {convertFiltersToQuery, parseEventsFiltersFromUrl} from 'modules/events/helpers/filters';
import {useRouter} from 'next/router';
import {IEventsResponseData} from 'modules/events/types';
import {useAppDispatch} from 'core/redux/hooks/useAppDispatch';
import {IPagination} from 'core/models';

interface IProps {
    children: ReactNode;
    defaultData?: IEventsResponseData;
}

interface IEventsState {
    data: IEventsResponseData | null;
    isLoading: boolean;
    currentPage: number;
    isNeedAppendData: boolean;
}

export const EventsListProvider: FC<IProps> = ({children, defaultData}) => {
    const dispatch = useAppDispatch();
    const router = useRouter();
    const {query, asPath, pathname} = router;

    const {slug: cityCode} = query;

    const [state, setState] = useState<IEventsState>({
        data: defaultData ?? null,
        isLoading: false,
        currentPage: query.page ? Number(query.page) : 1,
        isNeedAppendData: false,
    });

    const filtersQuery = useMemo(() => {
        const filters = parseEventsFiltersFromUrl(asPath);
        return convertFiltersToQuery(filters, cityCode as string);
    }, [asPath, cityCode]);

    const eventsFilterData = useGetEventsFilterQuery({query: filtersQuery});

    const fetchEvents = useCallback(
        async (page: number) => {
            setState((prev) => ({...prev, isLoading: true}));

            try {
                const response = await dispatch(getEvents.initiate({query: getEventsApiQuery(filtersQuery, page)}));
                const responseData = response.data?.data;

                if (!responseData) {
                    setState((prev) => ({
                        ...prev,
                        isLoading: false,
                        isNeedAppendData: false,
                    }));
                    return;
                }

                setState((prev) => ({
                    ...prev,
                    data:
                        1 === page || !prev.isNeedAppendData
                            ? responseData
                            : {
                                  ...responseData,
                                  events: [...(prev.data?.items ?? []), ...(responseData.items ?? [])],
                              },
                    isLoading: false,
                    isNeedAppendData: false,
                }));
            } catch (error) {
                setState((prev) => ({
                    ...prev,
                    isLoading: false,
                    isNeedAppendData: false,
                }));
            }
        },
        [dispatch, filtersQuery],
    );

    const loadMoreEvents = useCallback(() => {
        const nextPage = state.currentPage + 1;
        setState((prev) => ({...prev, isNeedAppendData: true}));
        router.push({pathname, query: {...query, page: nextPage}}, undefined, {
            shallow: true,
        });
    }, [state.currentPage, pathname, query, router]);

    useEffect(() => {
        if (query.page !== state.currentPage.toString()) {
            const newPage = query.page ? Number(query.page) : 1;
            setState((prev) => ({...prev, currentPage: newPage}));
            fetchEvents(newPage);
        }
    }, [query.page, state.currentPage, fetchEvents]);

    const value = useMemo(() => {
        if (!state.data?.items) {
            return {
                canLoadMore: false,
                filter: {},
                meta: {},
                events: [],
                eventsGroupedByDay: [],
                isLoading: state.isLoading,
                bannerToWineTesting: {isActive: false, url: ''},
            };
        }

        const {pageNumber, pageSize, totalCount} = state.data.pagination as IPagination;

        return {
            canLoadMore: pageNumber * pageSize < totalCount,
            filter: {...eventsFilterData.data?.data.items},
            meta: {...eventsFilterData.data?.meta},
            events: [...state.data.items],
            eventsGroupedByDay: getEventsGroupedByDay(state.data.items),
            loadMoreEvents,
            isLoading: state.isLoading,
            bannerToWineTesting: state.data.bannerToWineTesting,
        };
    }, [state.data, state.isLoading, eventsFilterData, loadMoreEvents]);

    return <EventsListContext.Provider value={value}>{children}</EventsListContext.Provider>;
};
